import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useMsal } from "@azure/msal-react";
import {
  Batch,
  deleteBatch,
  getBatches,
  getbatch,
  getMaterialCountReconciliation,
  getMaterialQtyReconciliation,
  getSupplierReconciliation,
  getHaulerReconciliation,
  reconcileBatch,
  MaterialCountRow,
  MaterialQtyRow,
  SupplierRow,
  HaulerRow,
  updateBatchDate,
  updateBatchColor,
  markBatchComplete,
} from "../services/batchApiService";
import { TableAction } from "../components/TanStackDynamicTable";
import {
  CoverSheetDetail,
  deleteCoverSheetDetails,
  getCoverSheet,
  upsertCoverSheetDetails,
} from "../services/coversheetApiService";
import {
  Building2,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Database,
  Edit,
  ExternalLink,
  Filter,
  Hash,
  Notebook,
  PlusIcon,
  Save,
  Scale,
  Search,
  Send,
  Trash2,
  Truck,
  UserPen,
  X,
} from "lucide-react";
import { CustomSelect } from "../components/CustomSelect";
import { getTicketMetadata, TicketMetadata } from "../services/scansApiService";
import { getTickets, getTicketIds, pushTicketsBatch } from "../services/ticketApiService";
import { useNavigate, useLocation } from "react-router-dom";
import { addNote, deleteNote, getNotes, Note, updateNote } from "../services/apiService";
import { NotesPane } from "../components/NotesPane";
import { ProDatePicker } from "../components/ProDatePicker";
import { useUser } from "../contexts/UserContext";
import { BatchColorSetting, getBatchColorSettings } from "../services/batchColorApiService";
import { ColorTagPicker, ColorOption } from "../components/ColorTagPicker";
import { DynamicIcon } from "../components/IconPicker";
import { BatchPreset, BatchPresetModal, loadPresets } from "../components/BatchPresetModal";

// ─── Types ────────────────────────────────────────────────────────────────────

type ReconcileTabKey = "materialCount" | "materialQty" | "supplier" | "hauler";

interface ReconcileTally {
  total: number;
  match: number;
  issues: number;
  missingCover: number;
  missingTickets: number;
}

// ─── Helper Components ────────────────────────────────────────────────────────

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; dot?: string }[];
}) {
  return (
    <div className="inline-flex items-center bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-0.5 h-8">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all ${
              active
                ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 shadow-sm"
                : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200"
            }`}
          >
            {opt.dot && (
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: opt.dot }} />
            )}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    Match: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    "Missing Cover Sheet": "bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400",
    "Missing Tickets": "bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400",
    Mismatch: "bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${
        styles[status] ?? styles.Match
      }`}
    >
      {status}
    </span>
  );
}

function VarCell({ value, format = "int" }: { value: number; format?: "int" | "qty" }) {
  const isZero = value === 0;
  const display =
    format === "qty"
      ? isZero ? "0.00" : value.toFixed(2)
      : isZero ? "0" : value > 0 ? `+${value}` : `${value}`;
  return (
    <span className={`tabular-nums font-mono ${isZero ? "text-gray-400 dark:text-slate-500 font-normal" : "text-red-500 dark:text-red-400 font-semibold"}`}>
      {display}
    </span>
  );
}

function tallyRows(rows: { status: string }[]): ReconcileTally {
  let match = 0, missingCover = 0, missingTickets = 0, other = 0;
  for (const r of rows) {
    if (r.status === "Match") match++;
    else if (r.status === "Missing Cover Sheet") missingCover++;
    else if (r.status === "Missing Tickets") missingTickets++;
    else other++;
  }
  return { total: rows.length, match, issues: missingCover + missingTickets + other, missingCover, missingTickets };
}

const RECONCILE_META: {
  key: ReconcileTabKey;
  label: string;
  sub: string;
  Icon: React.FC<{ size?: number; className?: string }>;
}[] = [
  { key: "materialCount", label: "Material Count", sub: "Tickets vs cover sheet count", Icon: Hash },
  { key: "materialQty", label: "Material Qty", sub: "Tonnage / gallons vs cover", Icon: Scale },
  { key: "supplier", label: "Supplier", sub: "Rolled up by supplier", Icon: Building2 },
  { key: "hauler", label: "Hauler", sub: "Rolled up by hauler", Icon: Truck },
];

function HealthCard({
  meta,
  t,
  active,
  onClick,
}: {
  meta: (typeof RECONCILE_META)[number];
  t: ReconcileTally;
  active: boolean;
  onClick: () => void;
}) {
  const hasIssues = t.issues > 0;
  return (
    <button
      onClick={onClick}
      className={`flex-1 min-w-0 text-left rounded-lg p-3 flex flex-col gap-2 transition-all border ${
        active
          ? "bg-blue-50 dark:bg-slate-800 border-blue-400 dark:border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.15)]"
          : "bg-gray-50 dark:bg-slate-800/60 border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600"
      }`}
    >
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
          active
            ? "bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400"
            : "bg-gray-200 dark:bg-slate-700/60 text-gray-500 dark:text-slate-400"
        }`}>
          <meta.Icon size={13} />
        </div>
        <span className="text-xs text-gray-600 dark:text-slate-400 font-medium truncate">{meta.label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-bold tabular-nums leading-none ${hasIssues ? "text-red-500 dark:text-red-400" : "text-gray-900 dark:text-slate-100"}`}>
          {t.issues}
        </span>
        <span className="text-[11px] text-gray-400 dark:text-slate-500">
          {hasIssues ? `of ${t.total} have issues` : `${t.total} all match`}
        </span>
      </div>
      <div className="flex gap-1 h-1 rounded-full overflow-hidden bg-gray-200 dark:bg-slate-700/40">
        {t.match > 0 && <div className="bg-gray-400 dark:bg-slate-500 opacity-50" style={{ flex: t.match }} />}
        {t.missingCover > 0 && <div className="bg-amber-400" style={{ flex: t.missingCover }} />}
        {t.missingTickets > 0 && <div className="bg-red-400" style={{ flex: t.missingTickets }} />}
      </div>
    </button>
  );
}

function ReconcileTable({
  rows,
  issuesOnly,
  renderRow,
  headers,
}: {
  rows: { status: string }[];
  issuesOnly: boolean;
  headers: { label: string; align?: "right"; width?: string }[];
  renderRow: (row: any, i: number) => React.ReactNode;
}) {
  const filtered = issuesOnly ? rows.filter((r) => r.status !== "Match") : rows;
  if (filtered.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <CheckCircle className="text-green-500 dark:text-green-400" size={28} />
        <div className="text-gray-800 dark:text-slate-200 font-medium text-sm">All rows reconcile</div>
        <div className="text-gray-400 dark:text-slate-500 text-xs">No variances on this reconcile</div>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-auto min-h-0">
      <table className="w-full border-collapse text-sm" style={{ tableLayout: "fixed" }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={h.width ? { width: h.width } : undefined}
                className={`sticky top-0 z-10 bg-gray-50 dark:bg-slate-800 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-500 border-b border-gray-200 dark:border-slate-700 whitespace-nowrap ${
                  h.align === "right" ? "text-right" : "text-left"
                }`}
              >
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((row, i) => (
            <tr key={i} className={i % 2 === 1 ? "bg-gray-50/60 dark:bg-white/[0.015]" : ""}>
              {renderRow(row, i)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const tdCls = (align?: "right") =>
  `px-3.5 py-1 border-b border-gray-100 dark:border-slate-700/50 whitespace-nowrap ${
    align === "right" ? "text-right" : "text-left"
  }`;

const monoCell = (val: string) => (
  <td className={tdCls()}>
    <span className="font-mono text-xs text-gray-700 dark:text-slate-200">{val}</span>
  </td>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const BatchPageV2: React.FC = () => {
  const { instance, accounts } = useMsal();
  const { isAdmin, entityConfig } = useUser();

  const enabledMethods = {
    materialCount: entityConfig?.useMaterialCount ?? true,
    materialQty:   entityConfig?.useMaterialQty   ?? true,
    supplier:      entityConfig?.useSupplier       ?? true,
    hauler:        entityConfig?.useHauler         ?? true,
  };
  const visibleMeta = RECONCILE_META.filter(m => enabledMethods[m.key]);
  const navigate = useNavigate();
  const location = useLocation();

  const [batchData, setBatchData] = useState<Batch[]>([]);
  const [batchCount, setBatchCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("batch_searchTerm") || "");
  const [statusFilter, setStatusFilter] = useState<string | null>(() => {
    const s = localStorage.getItem("batch_statusFilter");
    return s !== null ? (s === "null" ? null : s) : "Active";
  });
  const [issueFilter, setIssueFilter] = useState<string | null>(null);

  const [startDate, setStartDate] = useState<string | null>(() => localStorage.getItem("batch_startDate"));
  const [endDate, setEndDate] = useState<string | null>(() => localStorage.getItem("batch_endDate"));
  const [colorFilter, setColorFilter] = useState<string | null>(null);

  const [presets, setPresets] = useState<BatchPreset[]>(() => loadPresets());
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(
    () => localStorage.getItem("batch_selectedPresetId") || null,
  );
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const selectedPreset = presets.find((p) => p.id === selectedPresetId) ?? null;

  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [scenario, setScenario] = useState(1);

  const [coverSheetData, setCoverSheetData] = useState<CoverSheetDetail[]>([]);
  const [isCoverSheetLoading, setIsCoverSheetLoading] = useState(false);
  const [coverSheetCollapsed, setCoverSheetCollapsed] = useState(false);
  const [metadata, setMetadata] = useState<TicketMetadata | null>(null);

  const [materialCountData, setMaterialCountData] = useState<MaterialCountRow[]>([]);
  const [materialQtyData, setMaterialQtyData] = useState<MaterialQtyRow[]>([]);
  const [supplierData, setSupplierData] = useState<SupplierRow[]>([]);
  const [haulerData, setHaulerData] = useState<HaulerRow[]>([]);
  const [isReconcileLoading, setIsReconcileLoading] = useState(false);
  const [reconcileTab, setReconcileTab] = useState<ReconcileTabKey>(
    (RECONCILE_META.find(m => enabledMethods[m.key])?.key) ?? "materialCount"
  );
  const [issuesOnly, setIssuesOnly] = useState(false);

  const [colorSettings, setColorSettings] = useState<BatchColorSetting[]>([]);

  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [notesPinned, setNotesPinned] = useState(false);
  const [notesBatchId, setNotesBatchId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  const [showDateModal, setShowDateModal] = useState(false);
  const [newDate, setNewDate] = useState("");

  const totalPages = Math.ceil(batchCount / pageSize);

  const visibleColors = useMemo<ColorOption[]>(() => {
    return colorSettings
      .filter((s) => s.isEnabled)
      .map((s) => ({ value: s.colorValue, hexColor: s.hexColor || "#94a3b8", icon: s.icon, label: s.label || s.colorValue }));
  }, [colorSettings]);

  const tallies = useMemo(
    () => ({
      materialCount: tallyRows(materialCountData),
      materialQty: tallyRows(materialQtyData),
      supplier: tallyRows(supplierData),
      hauler: tallyRows(haulerData),
    }),
    [materialCountData, materialQtyData, supplierData, haulerData],
  );

  const uomOptions = ["TN", "GA", "EA"].map((u) => ({ label: u, value: u }));

  useEffect(() => { localStorage.setItem("batch_searchTerm", searchTerm); }, [searchTerm]);
  useEffect(() => { localStorage.setItem("batch_statusFilter", statusFilter === null ? "null" : statusFilter); }, [statusFilter]);

  useEffect(() => { startDate ? localStorage.setItem("batch_startDate", startDate) : localStorage.removeItem("batch_startDate"); }, [startDate]);
  useEffect(() => { endDate ? localStorage.setItem("batch_endDate", endDate) : localStorage.removeItem("batch_endDate"); }, [endDate]);
  useEffect(() => {
    if (selectedPresetId) localStorage.setItem("batch_selectedPresetId", selectedPresetId);
    else localStorage.removeItem("batch_selectedPresetId");
  }, [selectedPresetId]);

  const fetchBatchData = async (page: number) => {
    if (accounts.length === 0) return;
    try {
      setLoading(true);
      const result = await getBatches(
        instance, accounts[0], page, searchTerm, statusFilter, colorFilter,
        startDate, endDate,
        selectedPreset?.branches ?? null,
        selectedPreset?.warehouses ?? null,
        issueFilter,
      );
      setBatchData(result.data);
      setBatchCount(result.totalCount);
    } catch (error) {
      console.error("Failed to load batches", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshBatch = async (batchId: string) => {
    if (accounts.length === 0) return;
    try {
      const updated = await getbatch(instance, accounts[0], batchId);
      setBatchData((prev) => prev.map((b) => (b.batchId === batchId ? { ...b, ...updated } : b)));
      if (selectedBatch?.batchId === batchId) setSelectedBatch((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch (err) {
      console.error("Failed to refresh batch", err);
    }
  };

  const fetchCoverSheetData = async (batchId: string, sc: number) => {
    if (accounts.length === 0) return;
    setIsCoverSheetLoading(true);
    try {
      const result = await getCoverSheet(instance, accounts[0], batchId, sc);
      setCoverSheetData(result.data);
    } catch (error) {
      console.error("Failed to load cover sheet data", error);
    } finally {
      setIsCoverSheetLoading(false);
    }
  };

  const fetchMetadata = async () => {
    if (accounts.length === 0) return;
    try { setMetadata(await getTicketMetadata()); }
    catch (error) { console.error("Failed to load metadata", error); }
  };

  const fetchAllReconciles = useCallback(async (batchId: string) => {
    if (!accounts[0]) return;
    setIsReconcileLoading(true);
    try {
      const [mc, mq, sup, haul] = await Promise.all([
        enabledMethods.materialCount ? getMaterialCountReconciliation(instance, accounts[0], batchId) : Promise.resolve({ data: [] }),
        enabledMethods.materialQty   ? getMaterialQtyReconciliation(instance, accounts[0], batchId)   : Promise.resolve({ data: [] }),
        enabledMethods.supplier      ? getSupplierReconciliation(instance, accounts[0], batchId)      : Promise.resolve({ data: [] }),
        enabledMethods.hauler        ? getHaulerReconciliation(instance, accounts[0], batchId)        : Promise.resolve({ data: [] }),
      ]);
      setMaterialCountData(mc.data as MaterialCountRow[]);
      setMaterialQtyData(mq.data as MaterialQtyRow[]);
      setSupplierData(sup.data as SupplierRow[]);
      setHaulerData(haul.data as HaulerRow[]);

      const totalIssues =
        mc.data.filter((r: { status: string }) => r.status !== "Match").length +
        mq.data.filter((r: { status: string }) => r.status !== "Match").length +
        sup.data.filter((r: { status: string }) => r.status !== "Match").length +
        haul.data.filter((r: { status: string }) => r.status !== "Match").length;

      setBatchData((prev) => prev.map((b) => b.batchId === batchId ? { ...b, issueCount: totalIssues } : b));
      setSelectedBatch((prev) => prev?.batchId === batchId ? { ...prev, issueCount: totalIssues } : prev);

      // Persist the updated issueCount to the server in the background
      reconcileBatch(instance, accounts[0], batchId).catch((e) =>
        console.error("Failed to persist reconciliation:", e)
      );
    } catch (e) {
      console.error("Reconciliation fetch error:", e);
    } finally {
      setIsReconcileLoading(false);
    }
  }, [instance, accounts, enabledMethods.materialCount, enabledMethods.materialQty, enabledMethods.supplier, enabledMethods.hauler]);

  useEffect(() => { fetchMetadata(); }, []);

  useEffect(() => {
    if (accounts.length === 0) return;
    getBatchColorSettings(instance, accounts[0])
      .then(setColorSettings)
      .catch((e) => console.error("Failed to load color settings", e));
  }, [instance, accounts]);

  useEffect(() => { fetchBatchData(currentPage); }, [currentPage, statusFilter, colorFilter, issueFilter, startDate, endDate, selectedPresetId]);

  useEffect(() => {
    if (selectedBatch) fetchCoverSheetData(selectedBatch.batchId, scenario);
  }, [selectedBatch?.batchId, scenario]);

  useEffect(() => {
    if (selectedBatch) {
      fetchAllReconciles(selectedBatch.batchId);
    } else {
      setMaterialCountData([]); setMaterialQtyData([]); setSupplierData([]); setHaulerData([]);
    }
  }, [selectedBatch?.batchId]);

  useEffect(() => {
    const navState = location.state as { selectedBatchId?: string } | null;
    if (navState?.selectedBatchId && batchData.length > 0) {
      const match = batchData.find((b) => b.batchId === navState.selectedBatchId);
      if (match) setSelectedBatch(match);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [batchData, location.state]);

  useEffect(() => {
    if (isNotesOpen && notesBatchId && accounts[0]) {
      setNotesLoading(true);
      getNotes(instance, accounts[0], notesBatchId)
        .then((data) => setNotes(data || []))
        .catch(console.error)
        .finally(() => setNotesLoading(false));
    }
  }, [isNotesOpen, notesBatchId, instance, accounts]);

  const handleEdit = async () => {
    if (!selectedBatch) return;
    const batchFilter = `BatchID == "${selectedBatch.batchId}" && Status != "Deleted"`;
    const ids = await getTicketIds(instance, accounts[0], batchFilter);
    if (ids.length === 0) { alert("No tickets found for this batch."); return; }
    const queueKey = `queue_${Date.now()}`;
    sessionStorage.setItem(queueKey, JSON.stringify({ queue: ids, index: 0, returnPath: "/batch-v2", batchId: selectedBatch.batchId }));
    navigate(`/ticket/${ids[0]}?queueKey=${queueKey}`);
  };

  const handleDeleteBatch = async () => {
    if (!selectedBatch) return;
    if (!window.confirm("Are you sure you want to delete this batch?\nIt will soft delete all tickets and cover sheets will no longer be accessible.")) return;
    await deleteBatch(instance, accounts[0], selectedBatch.batchId);
    setBatchData((old) => old.filter((row) => row.batchId !== selectedBatch!.batchId));
    setSelectedBatch(null);
    alert("Successfully deleted tickets, batch, and coversheets.");
  };

  const handlePushBatch = async () => {
    if (!selectedBatch) return;
    if (!window.confirm("Are you sure you want to push this batch?")) return;
    try {
      const result = await getTickets(instance, accounts[0], `BatchID == "${selectedBatch.batchId}"`);
      const ids = result.data?.map((t: any) => Number(t.id)) || [];
      const res = await pushTicketsBatch(instance, accounts[0], ids);
      const results = res.results || [];
      const successes = results.filter((r: any) => r.success);
      const failures = results.filter((r: any) => !r.success);
      let alertMsg = `Push Results:\n✅ ${successes.length} Successful\n❌ ${failures.length} Failed`;
      if (failures.length > 0) alertMsg += `\n\nErrors:\n` + failures.map((f: any) => `Ticket ${f.ticketId}: ${f.error}`).join("\n");
      window.alert(alertMsg);
      await refreshBatch(selectedBatch.batchId);
    } catch (error) {
      console.error("Error pushing batch:", error);
      alert("Failed to push batch.");
    }
  };

  const handleDateChange = async () => {
    if (!selectedBatch || !newDate) return;
    try {
      await updateBatchDate(instance, accounts[0], selectedBatch.batchId, newDate);
      setShowDateModal(false);
      await fetchBatchData(currentPage);
      setSelectedBatch({ ...selectedBatch, orderDate: newDate });
    } catch (error) {
      console.error("Error updating batch date:", error);
      alert("Failed to update batch date.");
    }
  };

  const handleDeleteCoverSheet = async (selectedRows: any[]) => {
    const ids = selectedRows.map((r) => r.id);
    if (!window.confirm(`Are you sure you want to delete ${ids.length} selected row(s)?`)) return;
    try {
      await deleteCoverSheetDetails(instance, accounts[0], ids);
      setCoverSheetData((old) => old.filter((row) => !ids.includes(row.id)));
      if (selectedBatch) await fetchAllReconciles(selectedBatch.batchId);
    } catch (error) {
      console.error("Failed to delete cover sheet details", error);
    }
  };

  const handleSave = useCallback(async () => {
    setIsCoverSheetLoading(true);
    try {
      await upsertCoverSheetDetails(instance, accounts[0], coverSheetData);
      if (selectedBatch) {
        await fetchCoverSheetData(selectedBatch.batchId, scenario);
        await fetchAllReconciles(selectedBatch.batchId);
        await refreshBatch(selectedBatch.batchId);
      }
    } catch (error) {
      console.error("Failed to save cover sheet details", error);
    } finally {
      setIsCoverSheetLoading(false);
    }
  }, [instance, accounts, coverSheetData, selectedBatch, scenario]);

  const handleCellEdit = (rowIndex: number, columnId: string, value: any) => {
    setCoverSheetData((old) => old.map((row, i) => (i === rowIndex ? { ...row, [columnId]: value } : row)));
  };

  const handleCreateNote = async () => {
    if (!newNoteText.trim() || !notesBatchId || !accounts[0]) return;
    const newNote: Note = { id: 0, batchId: notesBatchId, author: accounts[0].name || "Unknown", createdDateTime: new Date(), note: newNoteText };
    try {
      await addNote(instance, accounts[0], newNote);
      setNewNoteText("");
      setNotes(await getNotes(instance, accounts[0], notesBatchId) || []);
    } catch (err) { console.error(err); alert("Failed to add note"); }
  };

  const handleUpdateNote = async (note: Note) => {
    if (!editingNoteText.trim() || !accounts[0]) return;
    try {
      await updateNote(instance, accounts[0], { ...note, note: editingNoteText });
      setEditingNoteId(null); setEditingNoteText("");
      if (notesBatchId) setNotes(await getNotes(instance, accounts[0], notesBatchId) || []);
    } catch (err) { console.error(err); alert("Failed to update note"); }
  };

  const handleDeleteNote = async (note: Note) => {
    if (!accounts[0] || !window.confirm("Delete this note?")) return;
    try {
      await deleteNote(instance, accounts[0], note.id);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
    } catch (err) { console.error(err); alert("Failed to delete note"); }
  };

  const reconciliationActions: TableAction[] = useMemo(() => {
    const actions: TableAction[] = [
      { label: "Review Tickets", icon: <Edit size={16} className="text-blue-500" />, onClick: handleEdit },
      {
        label: "View in Pending",
        icon: <ExternalLink size={16} className="text-blue-500" />,
        onClick: () => { if (selectedBatch) navigate(`/pending?batchId.equals=${selectedBatch.batchId}`); },
      },
      { label: "Push Batch", icon: <Send size={16} className="text-emerald-500" />, onClick: handlePushBatch },
      {
        label: "Mark Complete",
        icon: <CheckCircle size={16} className="text-emerald-500" />,
        onClick: async () => {
          if (!selectedBatch) return;
          if (!window.confirm("Mark this batch as completed?")) return;
          try { await markBatchComplete(instance, accounts[0], selectedBatch.batchId); await refreshBatch(selectedBatch.batchId); }
          catch { alert("Failed to mark batch as completed."); }
        },
      },
      { label: "Delete Batch", icon: <Trash2 size={16} className="text-red-500" />, onClick: handleDeleteBatch },
      {
        label: "Change Date",
        icon: <Calendar size={16} className="text-primary" />,
        onClick: () => { if (selectedBatch) { setNewDate(selectedBatch.orderDate); setShowDateModal(true); } },
      },
    ];
    return actions;
    // return isAdmin ? actions : actions.filter((a) => a.label !== "Delete Batch");
  }, [handleEdit, isAdmin, selectedBatch]);


  const activeReconcileRows: { status: string }[] = useMemo(() => {
    if (reconcileTab === "materialCount") return materialCountData;
    if (reconcileTab === "materialQty") return materialQtyData;
    if (reconcileTab === "supplier") return supplierData;
    return haulerData;
  }, [reconcileTab, materialCountData, materialQtyData, supplierData, haulerData]);

  const reconcileHeaders: Record<ReconcileTabKey, { label: string; align?: "right"; width?: string }[]> = {
    materialCount: [
      { label: "Material", width: "30%" }, { label: "Cover Sheet", align: "right", width: "15%" }, { label: "Tickets", align: "right", width: "15%" },
      { label: "Δ", align: "right", width: "12%" }, { label: "Status", width: "28%" },
    ],
    materialQty: [
      { label: "Material", width: "30%" }, { label: "Cover Sheet", align: "right", width: "15%" }, { label: "Tickets", align: "right", width: "15%" },
      { label: "Δ", align: "right", width: "12%" }, { label: "Status", width: "28%" },
    ],
    supplier: [
      { label: "Supplier ID", width: "30%" }, { label: "Cover Sheet", align: "right", width: "15%" }, { label: "Tickets", align: "right", width: "15%" },
      { label: "Δ", align: "right", width: "12%" }, { label: "Status", width: "28%" },
    ],
    hauler: [
      { label: "Hauler ID", width: "30%" }, { label: "Cover Sheet", align: "right", width: "15%" }, { label: "Tickets", align: "right", width: "15%" },
      { label: "Δ", align: "right", width: "12%" }, { label: "Status", width: "28%" },
    ],
  };

  const numCell = (val: string | number) => (
    <td className={tdCls("right")}>
      <span className="tabular-nums text-gray-700 dark:text-slate-300">{val}</span>
    </td>
  );

  const renderReconcileRow = (row: any) => {
    if (reconcileTab === "materialCount") return (
      <>
        {monoCell(row.inventoryId)}
        {numCell(row.countCover)}
        {numCell(row.countScanned)}
        <td className={tdCls("right")}><VarCell value={row.countVariance} format="int" /></td>
        <td className={tdCls()}><StatusPill status={row.status} /></td>
      </>
    );
    if (reconcileTab === "materialQty") return (
      <>
        {monoCell(row.inventoryId)}
        {numCell(Number(row.receivedQtyCover).toFixed(2))}
        {numCell(Number(row.receivedQtyScanned).toFixed(2))}
        <td className={tdCls("right")}><VarCell value={row.receivedQtyVariance} format="qty" /></td>
        <td className={tdCls()}><StatusPill status={row.status} /></td>
      </>
    );
    if (reconcileTab === "supplier") return (
      <>
        {monoCell(row.vendorId)}
        {numCell(row.countCover)}
        {numCell(row.countScanned)}
        <td className={tdCls("right")}><VarCell value={row.countVariance} format="int" /></td>
        <td className={tdCls()}><StatusPill status={row.status} /></td>
      </>
    );
    return (
      <>
        {monoCell(row.freightVendorId)}
        {numCell(row.countCover)}
        {numCell(row.countScanned)}
        <td className={tdCls("right")}><VarCell value={row.countVariance} format="int" /></td>
        <td className={tdCls()}><StatusPill status={row.status} /></td>
      </>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full w-full bg-gray-50 dark:bg-background-dark text-gray-900 dark:text-slate-100 overflow-hidden transition-colors">

      {/* ── Filter Bar ── */}
      <div className="flex items-center gap-2.5 px-4 py-2 bg-white dark:bg-surface-dark border-b border-gray-200 dark:border-slate-700/60 flex-wrap shadow-sm dark:shadow-none">
        {/* Search */}
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-input-dark border border-gray-300 dark:border-slate-700 rounded-md px-2.5 h-8 flex-shrink-0">
          <Search size={13} className="text-gray-400 dark:text-slate-400" />
          <input
            placeholder="Search batches..."
            className="bg-transparent border-none outline-none text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 text-sm w-44"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchBatchData(currentPage); }}
          />
        </div>

        <Segmented
          value={(statusFilter ?? "null") as any}
          onChange={(v) => { setStatusFilter(v === "null" ? null : v); setCurrentPage(1); }}
          options={[
            { value: "Active", label: "Active" },
            { value: "Completed", label: "Complete" },
            { value: "null", label: "All" },
          ]}
        />

        <Segmented
          value={(issueFilter ?? "null") as any}
          onChange={(v) => { setIssueFilter(v === "null" ? null : v); setCurrentPage(1); }}
          options={[
            { value: "HasIssues", label: "Has issues", dot: "#ef4444" },
            { value: "NoIssues", label: "Clean" },
            { value: "Unreconciled", label: "New" },
            { value: "null", label: "All" },
          ]}
        />

        {/* Preset */}
        <div className="inline-flex items-center bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-0.5 h-8 flex-shrink-0">
          <div className="w-36">
            <CustomSelect
              value={selectedPresetId}
              options={[{ label: "All presets", value: "" }, ...presets.map((p) => ({ label: p.name, value: p.id }))]}
              onChange={(val) => { setSelectedPresetId(val === "" ? null : val); setCurrentPage(1); }}
              placeholder="All presets"
              chipStyle
            />
          </div>
          <span className="w-px h-4 bg-gray-200 dark:bg-slate-600 mx-0.5" />
          <button onClick={() => setPresetModalOpen(true)}
            className="inline-flex items-center px-1.5 py-1 rounded text-gray-400 dark:text-slate-500 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors">
            <Filter size={12} />
          </button>
        </div>

        {/* Color filter */}
        {visibleColors.length > 0 && (
          <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-input-dark border border-gray-300 dark:border-slate-700 rounded-md px-2 h-8">
            {visibleColors.map((color) => (
              <button key={color.value}
                onClick={() => { setColorFilter(colorFilter === color.value ? null : color.value); setCurrentPage(1); }}
                className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${colorFilter === color.value ? "ring-2 ring-offset-1 ring-offset-white dark:ring-offset-slate-900 scale-110" : "opacity-60 hover:opacity-100 hover:scale-110"}`}
                style={{ backgroundColor: color.hexColor }} title={color.label}>
                {color.icon && <DynamicIcon name={color.icon} size={10} className="text-white" />}
              </button>
            ))}
            {colorFilter && (
              <button onClick={() => { setColorFilter(null); setCurrentPage(1); }}
                className="text-[10px] text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 ml-0.5">
                clear
              </button>
            )}
          </div>
        )}

        {/* Date range */}
        <div className="flex items-center gap-2">
          <div className="w-32"><ProDatePicker value={startDate} onChange={(v) => { setStartDate(v || null); setCurrentPage(1); }} /></div>
          <span className="text-gray-400 dark:text-slate-500 text-xs">to</span>
          <div className="w-32"><ProDatePicker value={endDate} onChange={(v) => { setEndDate(v || null); setCurrentPage(1); }} /></div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Batch List Sidebar ── */}
        <aside className="w-56 flex-shrink-0 bg-white dark:bg-surface-dark border-r border-gray-200 dark:border-slate-700/60 flex flex-col shadow-sm dark:shadow-none">
          <div className="px-3 py-2.5 border-b border-gray-200 dark:border-slate-700/60 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-slate-400">Batches</span>
            <span className="text-[11px] text-gray-400 dark:text-slate-500 px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 rounded">{batchCount}</span>
          </div>

          <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1 bg-gray-50 dark:bg-slate-900/30">
            {loading ? (
              <div className="flex justify-center py-10 text-gray-400 dark:text-slate-500 text-sm italic">Loading…</div>
            ) : (
              batchData.map((batch) => {
                const isSelected = selectedBatch?.batchId === batch.batchId;
                const isNew = batch.issueCount === -1;
                const hasIssues = !isNew && batch.issueCount > 0;
                const badgeClass = isNew
                  ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400"
                  : hasIssues
                  ? "bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400"
                  : "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";//TODO
                const badgeLabel = isNew
                  ? batch.status
                  : `${batch.issueCount} issue${batch.issueCount !== 1 ? "s" : ""}`;

                return (
                  <button
                    key={batch.batchId}
                    onClick={() => setSelectedBatch(batch)}
                    className={`w-full text-left rounded-md px-2.5 py-2 border flex flex-col gap-1.5 transition-all relative ${
                      isSelected
                        ? "bg-blue-50 dark:bg-slate-800 border-blue-400 dark:border-blue-500 ring-1 ring-blue-400 dark:ring-blue-500/50"
                        : "bg-white dark:bg-slate-800/40 border-gray-200 dark:border-slate-700/60 hover:border-gray-300 dark:hover:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <span className={`font-mono text-xs font-semibold ${isSelected ? "text-blue-600 dark:text-blue-400" : "text-gray-800 dark:text-slate-200"}`}>
                      {batch.batchId}
                    </span>
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[11px] text-gray-500 dark:text-slate-500 truncate">{batch.branch} · {batch.warehouse}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 ${badgeClass}`}>
                        {badgeLabel}
                      </span>
                    </div>

                    <div className="flex items-center justify-end gap-1 mt-0.5" onClick={(e) => e.stopPropagation()}>
                      <ColorTagPicker
                        value={batch.colorTag}
                        colors={visibleColors}
                        onChange={async (newColor) => {
                          try {
                            await updateBatchColor(instance, accounts[0], batch.batchId, newColor);
                            const label = visibleColors.find((c) => c.value === newColor)?.label ?? "None";
                            await addNote(instance, accounts[0], { id: 0, batchId: batch.batchId, author: accounts[0].name || "Unknown", createdDateTime: new Date(), note: `Set Status to ${label}` });
                            setBatchData((prev) => prev.map((b) => b.batchId === batch.batchId ? { ...b, colorTag: newColor, noteCount: (b.noteCount ?? 0) + 1 } : b));
                            if (selectedBatch?.batchId === batch.batchId) setSelectedBatch((prev) => prev ? { ...prev, colorTag: newColor } : prev);
                            if (notesBatchId === batch.batchId) setNotes(await getNotes(instance, accounts[0], batch.batchId) || []);
                          } catch (err) { console.error("Error updating color tag:", err); }
                        }}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (notesPinned && notesBatchId === batch.batchId) { setNotesPinned(false); setIsNotesOpen(false); setNotesBatchId(null); }
                          else { setNotesBatchId(batch.batchId); setIsNotesOpen(true); setNotesPinned(true); }
                        }}
                        onMouseEnter={() => { if (!notesPinned) { setNotesBatchId(batch.batchId); setIsNotesOpen(true); } }}
                        onMouseLeave={() => { if (!notesPinned) { setIsNotesOpen(false); setNotesBatchId(null); } }}
                        className={`p-1 rounded transition-all relative ${
                          notesPinned && notesBatchId === batch.batchId
                            ? "bg-primary text-white"
                            : "text-gray-400 dark:text-slate-500 hover:text-primary hover:bg-gray-100 dark:hover:bg-slate-700"
                        }`}
                        title="Notes"
                      >
                        <Notebook size={14} />
                        {batch.noteCount > 0 && (
                          <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-primary border border-white dark:border-slate-900 rounded-full" />
                        )}
                      </button>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="px-3 py-2 border-t border-gray-200 dark:border-slate-700/60 bg-white dark:bg-surface-dark flex items-center justify-between">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}
              className="text-[11px] px-2 py-1 rounded border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Prev
            </button>
            <span className="text-[11px] text-gray-400 dark:text-slate-500">{currentPage} / {totalPages || 1}</span>
            <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}
              className="text-[11px] px-2 py-1 rounded border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Next
            </button>
          </div>
        </aside>

        {/* ── Main Content ── */}
        <main className="flex-1 overflow-hidden flex flex-col relative bg-white dark:bg-slate-900">
          {selectedBatch ? (
            <>
              {/* Batch header */}
              <div className="px-5 pt-3.5 pb-2.5 flex items-center gap-3.5 border-b border-gray-200 dark:border-slate-700/60 flex-wrap">
                <span className="font-mono text-lg font-bold text-blue-600 dark:text-blue-400">{selectedBatch.batchId}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                  selectedBatch.issueCount > 0
                    ? "bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400"
                    : "bg-gray-100 dark:bg-slate-700/60 text-gray-500 dark:text-slate-400"
                }`}>
                  {selectedBatch.issueCount > 0 ? `${selectedBatch.issueCount} Issues` : "Clean"}
                </span>
                <div className="w-px h-4 bg-gray-200 dark:bg-slate-700" />
                <div className="flex items-center gap-1.5 text-gray-500 dark:text-slate-400 text-sm">
                  <Calendar size={13} />
                  <span className="font-mono">{selectedBatch.orderDate}</span>
                </div>
                <div className="w-px h-4 bg-gray-200 dark:bg-slate-700" />
                <div className="flex items-center gap-1.5 text-gray-500 dark:text-slate-400 text-sm">
                  <Building2 size={13} />
                  <span className="font-mono">{selectedBatch.branch} · {selectedBatch.warehouse}</span>
                </div>
              </div>

              {/* Actions bar */}
              <div className="px-5 py-2 flex items-center gap-1.5 flex-wrap border-b border-gray-200 dark:border-slate-700/60">
                {reconciliationActions.map((a, i) => {
                  const isDanger = a.label === "Delete Batch";
                  return (
                    <button
                      key={i}
                      onClick={() => typeof a.onClick === "function" && a.onClick([])}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                        isDanger
                          ? "border-transparent text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:border-red-200 dark:hover:border-red-500/30"
                          : "border-transparent text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:border-gray-200 dark:hover:border-slate-700 hover:text-gray-800 dark:hover:text-slate-200"
                      }`}
                    >
                      {a.icon}
                      {a.label}
                    </button>
                  );
                })}
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-hidden flex flex-col">

                {/* Reconciliation section */}
                <div className="flex-1 min-h-0 flex flex-col border-b border-gray-200 dark:border-slate-700/60 overflow-hidden">
                  <div className="flex items-center justify-between px-5 pt-3 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-500">Reconciliation</span>
                    
                  </div>

                  {isReconcileLoading ? (
                    <div className="px-5 py-8 text-gray-400 dark:text-slate-500 text-sm italic text-center">Loading reconciles…</div>
                  ) : (
                    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                      {/* Tab strip + issues toggle */}
                      <div className="flex items-center justify-between px-5 gap-3">
                        <div className="flex gap-0.5">
                          {visibleMeta.map((m) => {
                            const isActive = reconcileTab === m.key;
                            const issues = tallies[m.key].issues;
                            return (
                              <button
                                key={m.key}
                                onClick={() => setReconcileTab(m.key)}
                                className={`inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium border-b-2 transition-all ${
                                  isActive
                                    ? "text-gray-900 dark:text-slate-100 border-blue-500"
                                    : "text-gray-500 dark:text-slate-400 border-transparent hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-600"
                                }`}
                              >
                                {m.label}
                                {issues > 0 && (
                                  <span className="tabular-nums text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400">
                                    {issues}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        {/* Issues only toggle */}
                        <label className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer select-none text-xs font-medium transition-all ${
                          issuesOnly
                            ? "bg-blue-50 dark:bg-blue-500/15 border-blue-300 dark:border-blue-500 text-blue-600 dark:text-blue-300"
                            : "border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-gray-300 dark:hover:border-slate-600 hover:text-gray-700 dark:hover:text-slate-200"
                        }`}>
                          <span className={`relative w-7 h-4 rounded-full transition-colors flex-shrink-0 ${issuesOnly ? "bg-blue-500" : "bg-gray-300 dark:bg-slate-600"}`}>
                            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${issuesOnly ? "left-3.5" : "left-0.5"}`} />
                          </span>
                          <input type="checkbox" className="sr-only" checked={issuesOnly} onChange={(e) => setIssuesOnly(e.target.checked)} />
                          Issues only
                          {tallies[reconcileTab].issues > 0 && (
                            <span className="text-red-500 dark:text-red-400 font-bold">· {tallies[reconcileTab].issues}</span>
                          )}
                        </label>
                      </div>

                      <div className="h-px bg-gray-200 dark:bg-slate-700/60 mx-5" />

                      <ReconcileTable
                        rows={activeReconcileRows}
                        issuesOnly={issuesOnly}
                        headers={reconcileHeaders[reconcileTab]}
                        renderRow={renderReconcileRow}
                      />
                    </div>
                  )}
                </div>

                {/* Cover Sheet section */}
                <div className={`${coverSheetCollapsed ? 'flex-shrink-0' : 'flex-1 min-h-0'} px-4 py-3 bg-gray-100 dark:bg-slate-950 border-t border-gray-200 dark:border-slate-700/80`}>
                  <div className={`rounded-lg border border-gray-300 dark:border-slate-600 overflow-hidden shadow-sm bg-white dark:bg-slate-800 flex flex-col ${coverSheetCollapsed ? '' : 'h-full'}`}>
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-slate-700/60 bg-gray-50 dark:bg-slate-700/40">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCoverSheetCollapsed(!coverSheetCollapsed)}
                        className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
                      >
                        {coverSheetCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                      </button>
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-500">Cover Sheet</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => setScenario(1)}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${scenario === 1 ? "text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-500/10" : "text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800"}`}
                      >
                        <UserPen size={12} /> Accounting
                      </button>
                      <button
                        onClick={() => setScenario(0)}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${scenario === 0 ? "text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-500/10" : "text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800"}`}
                      >
                        <Database size={12} /> Plant
                      </button>
                      {scenario === 1 && (
                        <>
                          <span className="w-px h-4 bg-gray-200 dark:bg-slate-600 mx-1" />
                          <button
                            onClick={() => setCoverSheetData((old) => [{
                              id: 0, warehouse: selectedBatch?.warehouse || "", branch: selectedBatch?.branch || "",
                              inventoryID: "", vendorID: "", freightVendorID: "", loads: 0, receivedQty: 0, uom: "TN",
                              scenario: 1, orderDate: selectedBatch?.orderDate || new Date().toISOString().split("T")[0],
                              batchId: selectedBatch?.batchId || "", createdTimestamp: new Date().toISOString(),
                            }, ...old])}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors"
                          >
                            <PlusIcon size={12} /> Add
                          </button>
                          <button
                            onClick={handleSave}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-primary hover:bg-primary/10 transition-colors"
                          >
                            <Save size={12} /> Save
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {!coverSheetCollapsed && (
                    <div className="flex-1 overflow-auto min-h-0">
                      {isCoverSheetLoading ? (
                        <div className="flex justify-center py-6 text-gray-400 dark:text-slate-500 text-sm italic">Loading…</div>
                      ) : (
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-slate-700/60 bg-gray-50 dark:bg-slate-700/30">
                              <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 px-3 py-2">Material</th>
                              <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 px-3 py-2">Supplier</th>
                              <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 px-3 py-2">Hauler</th>
                              <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 px-3 py-2">Loads</th>
                              <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 px-3 py-2">Qty</th>
                              <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 px-3 py-2">UOM</th>
                              {scenario === 1 && <th className="w-8" />}
                            </tr>
                          </thead>
                          <tbody>
                            {coverSheetData.length === 0 ? (
                              <tr>
                                <td colSpan={scenario === 1 ? 7 : 6} className="px-3 py-6 text-center text-gray-400 dark:text-slate-500 italic">No cover sheet data</td>
                              </tr>
                            ) : coverSheetData.map((row, i) => (
                              <tr key={row.id || i} className="border-b border-gray-100 dark:border-slate-700/50 hover:bg-gray-50/60 dark:hover:bg-slate-800/30">
                                <td className="px-1 py-1">
                                  {scenario === 1 ? (
                                    <select value={row.inventoryID} onChange={(e) => handleCellEdit(i, "inventoryID", e.target.value)}
                                      className="w-full px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded text-sm text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-primary focus:border-primary">
                                      <option value="">—</option>
                                      {metadata?.coverSheetInventoryItems.map((item) => (
                                        <option key={item.inventoryId} value={item.inventoryId}>{item.inventoryId} - {item.inventoryDesc}</option>
                                      ))}
                                    </select>
                                  ) : <span className="px-2 text-gray-900 dark:text-slate-100">{row.inventoryID || "—"}</span>}
                                </td>
                                <td className="px-1 py-1">
                                  {scenario === 1 ? (
                                    <select value={row.vendorID} onChange={(e) => handleCellEdit(i, "vendorID", e.target.value)}
                                      className="w-full px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded text-sm text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-primary focus:border-primary">
                                      <option value="">—</option>
                                      {metadata?.vendors.map((item) => (
                                        <option key={item.vendorId} value={item.vendorId}>{item.vendorId} - {item.vendorIddesc}</option>
                                      ))}
                                    </select>
                                  ) : <span className="px-2 text-gray-900 dark:text-slate-100">{row.vendorID || "—"}</span>}
                                </td>
                                <td className="px-1 py-1">
                                  {scenario === 1 ? (
                                    <select value={row.freightVendorID} onChange={(e) => handleCellEdit(i, "freightVendorID", e.target.value)}
                                      className="w-full px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded text-sm text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-primary focus:border-primary">
                                      <option value="">—</option>
                                      {metadata?.freightVendors.map((item) => (
                                        <option key={item.freightVendorId} value={item.freightVendorId}>{item.freightVendorId} - {item.freightVendorIddesc}</option>
                                      ))}
                                    </select>
                                  ) : <span className="px-2 text-gray-900 dark:text-slate-100">{row.freightVendorID || "—"}</span>}
                                </td>
                                <td className="px-1 py-1 text-right">
                                  {scenario === 1 ? (
                                    <input type="number" value={row.loads} onChange={(e) => handleCellEdit(i, "loads", Number(e.target.value))}
                                      className="w-20 px-1.5 py-0.5 text-right bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary text-slate-900 dark:text-white" />
                                  ) : <span className="px-2 tabular-nums text-gray-900 dark:text-slate-100">{row.loads ?? "—"}</span>}
                                </td>
                                <td className="px-1 py-1 text-right">
                                  {scenario === 1 ? (
                                    <input type="number" value={row.receivedQty} onChange={(e) => handleCellEdit(i, "receivedQty", Number(e.target.value))}
                                      className="w-24 px-1.5 py-0.5 text-right bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary text-slate-900 dark:text-white" />
                                  ) : <span className="px-2 tabular-nums text-gray-900 dark:text-slate-100">{row.receivedQty ?? "—"}</span>}
                                </td>
                                <td className="px-1 py-1">
                                  {scenario === 1 ? (
                                    <select value={row.uom} onChange={(e) => handleCellEdit(i, "uom", e.target.value)}
                                      className="w-20 px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded text-sm text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-primary focus:border-primary">
                                      {uomOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                  ) : <span className="px-2 text-gray-900 dark:text-slate-100">{row.uom || "—"}</span>}
                                </td>
                                {scenario === 1 && (
                                  <td className="px-2 py-1">
                                    <button onClick={() => handleDeleteCoverSheet([row])}
                                      className="text-gray-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                                      <X size={13} />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                  </div>
                </div>

              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-slate-500 text-sm italic">
              Select a batch from the sidebar to view reconciliation data.
            </div>
          )}

          <NotesPane
            isOpen={isNotesOpen}
            onClose={() => { setIsNotesOpen(false); setNotesPinned(false); setNotesBatchId(null); }}
            notes={notes}
            loading={notesLoading}
            currentUserName={accounts[0]?.name}
            newNoteText={newNoteText}
            setNewNoteText={setNewNoteText}
            onCreateNote={handleCreateNote}
            editingNoteId={editingNoteId}
            setEditingNoteId={setEditingNoteId}
            editingNoteText={editingNoteText}
            setEditingNoteText={setEditingNoteText}
            onUpdateNote={handleUpdateNote}
            onDeleteNote={handleDeleteNote}
          />
        </main>
      </div>

      {/* Change Date Modal */}
      {showDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-2xl p-6 w-80">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-gray-900 dark:text-slate-100">Change Batch Date</h2>
              <button onClick={() => setShowDateModal(false)} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-200">
                <X size={16} />
              </button>
            </div>
            <ProDatePicker value={newDate} onChange={(val) => setNewDate(val)} />
            <div className="flex justify-end gap-2.5 mt-4">
              <button onClick={() => setShowDateModal(false)}
                className="px-3.5 py-2 text-sm text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                Cancel
              </button>
              <button onClick={handleDateChange}
                className="px-3.5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <BatchPresetModal
        isOpen={presetModalOpen}
        onClose={() => setPresetModalOpen(false)}
        onPresetsChanged={(next) => {
          setPresets(next);
          if (selectedPresetId && !next.some((p) => p.id === selectedPresetId)) setSelectedPresetId(null);
        }}
      />
    </div>
  );
};

export default BatchPageV2;
